// src/modules/stock/Scanner/Scanner.js
import React, { useState, useEffect, useRef } from "react";
import {
    Card, Row, Col, Tabs, Tab, Button, Alert,
    Spinner, Badge
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../../../layouts/Footer";
import Header from "../../../layouts/Header";
import ScannerCamera from "./ScannerCamera";
import ScannerManuel from "./ScannerManuel";
import ResultatScan from "./ResultatScan";
import HistoriqueScans from "./HistoriqueScans";
import { formatCurrency } from "../../../utils/formatters";

export default function Scanner() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("camera");
    const [scanResult, setScanResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [mode, setMode] = useState('recherche');
    const [quantite, setQuantite] = useState(1);
    const [commentaire, setCommentaire] = useState('');
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [alertes, setAlertes] = useState([]);
    
    // ✅ Référence pour forcer le rafraîchissement de l'historique
    const [historiqueKey, setHistoriqueKey] = useState(Date.now());
    const historiqueRef = useRef(null);

    const getToken = () => {
        return localStorage.getItem("token");
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const token = getToken();
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/scanner.php?action=stats',
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            if (result.success) {
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    const handleScan = async (code) => {
        setLoading(true);
        setError(null);
        setAlertes([]);
        
        try {
            const token = getToken();
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/scanner.php?action=rechercher&code=${encodeURIComponent(code)}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setScanResult(result.data);
                
                const alertesList = [];
                if (result.data.est_en_alerte) {
                    alertesList.push({
                        type: 'danger',
                        message: `⚠️ Stock critique : ${result.data.stock_actuel} unités (seuil: ${result.data.seuil_alerte})`
                    });
                }
                if (result.data.stock_actuel === 0) {
                    alertesList.push({
                        type: 'warning',
                        message: '📦 Produit en rupture de stock'
                    });
                }
                setAlertes(alertesList);
            } else {
                setError(result.message || "Produit non trouvé");
                setScanResult(null);
            }
        } catch (err) {
            setError(err.message || "Erreur lors du scan");
            console.error("Erreur scan:", err);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Fonction pour rafraîchir l'historique
    const refreshHistorique = () => {
        setHistoriqueKey(Date.now());
        // Si le composant HistoriqueScans expose une méthode refresh
        if (historiqueRef.current && historiqueRef.current.refresh) {
            historiqueRef.current.refresh();
        }
    };

    const handleEntreeStock = async () => {
        if (!scanResult || quantite <= 0) return;
        
        setLoading(true);
        try {
            const token = getToken();
            const userStr = localStorage.getItem("user");
            const user = JSON.parse(userStr);
            
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/scanner.php',
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'entree',
                        code_barre: scanResult.code_barre || scanResult.reference,
                        quantite: parseInt(quantite),
                        commentaire: commentaire,
                        id_utilisateur: user.id_utilisateur || user.id
                    })
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setSuccessMessage(`✅ ${result.data.quantite_ajoutee} ${scanResult.designation} ajouté(s) au stock`);
                setShowSuccessAlert(true);
                setTimeout(() => setShowSuccessAlert(false), 3000);
                
                // Rafraîchir le résultat
                await handleScan(scanResult.code_barre || scanResult.reference);
                setQuantite(1);
                setCommentaire('');
                await fetchStats();
                
                // ✅ Rafraîchir l'historique
                refreshHistorique();
                
            } else {
                setError(result.message || "Erreur lors de l'entrée");
            }
        } catch (err) {
            setError(err.message || "Erreur lors de l'entrée");
            console.error("Erreur entrée stock:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSortieStock = async () => {
        if (!scanResult || quantite <= 0) return;
        
        setLoading(true);
        try {
            const token = getToken();
            const userStr = localStorage.getItem("user");
            const user = JSON.parse(userStr);
            
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/scanner.php',
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'sortie',
                        code_barre: scanResult.code_barre || scanResult.reference,
                        quantite: parseInt(quantite),
                        commentaire: commentaire,
                        id_utilisateur: user.id_utilisateur || user.id
                    })
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setSuccessMessage(`✅ ${result.data.quantite_retiree} ${scanResult.designation} retiré(s) du stock`);
                setShowSuccessAlert(true);
                setTimeout(() => setShowSuccessAlert(false), 3000);
                
                // Rafraîchir le résultat
                await handleScan(scanResult.code_barre || scanResult.reference);
                setQuantite(1);
                setCommentaire('');
                await fetchStats();
                
                // ✅ Rafraîchir l'historique
                refreshHistorique();
                
                if (result.data.alerte) {
                    setAlertes([{ type: 'warning', message: result.data.alerte }]);
                }
            
            } else {
                setError(result.message || "Erreur lors de la sortie");
            }
        } catch (err) {
            setError(err.message || "Erreur lors de la sortie");
            console.error("Erreur sortie stock:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleVoirProduit = () => {
        if (scanResult) {
            navigate(`/stock/produits/${scanResult.id_produit}`);
        }
    };

    const handleVoirHistorique = () => {
        setActiveTab("historique");
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setQuantite(1);
        setCommentaire('');
        setError(null);
    };

    const getStockStatusColor = (stock, seuil) => {
        if (stock <= 0) return 'danger';
        if (stock <= seuil) return 'warning';
        return 'success';
    };

    const getStockProgress = (stock, max = 100) => {
        return Math.min((stock / max) * 100, 100);
    };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible>
                            <i className="ri-checkbox-circle-line me-2"></i>{successMessage}
                        </Alert>
                    )}

                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={8}>
                            <div>
                                <h1 className="page-title mb-1">
                                    <i className="ri-qr-code-line me-2"></i>
                                    Scanner codes-barres
                                </h1>
                                <p className="text-muted">
                                    Scannez un code-barres pour rechercher, ajouter ou retirer du stock
                                </p>
                            </div>
                        </Col>
                        <Col md={4} className="text-end">
                            <div className="d-flex gap-2 justify-content-end">
                                <Button 
                                    variant={mode === 'recherche' ? 'primary' : 'outline-primary'}
                                    onClick={() => handleModeChange('recherche')}
                                >
                                    <i className="ri-search-line me-2"></i>
                                    Recherche
                                </Button>
                                <Button 
                                    variant={mode === 'entree' ? 'success' : 'outline-success'}
                                    onClick={() => handleModeChange('entree')}
                                >
                                    <i className="ri-add-circle-line me-2"></i>
                                    Entrée
                                </Button>
                                <Button 
                                    variant={mode === 'sortie' ? 'danger' : 'outline-danger'}
                                    onClick={() => handleModeChange('sortie')}
                                >
                                    <i className="ri-subtract-line me-2"></i>
                                    Sortie
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} md={3}>
                                <Card className="border-0 shadow-sm bg-primary bg-opacity-10">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-primary bg-opacity-25 p-3 rounded-3 me-3">
                                                <i className="ri-qr-code-line text-primary fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Scans aujourd'hui</small>
                                                <h3 className="mb-0">{stats.aujourdhui || 0}</h3>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col sm={6} md={3}>
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-stack-line text-success fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Top scan</small>
                                                <h6 className="mb-0 text-truncate" style={{maxWidth: '150px'}}>
                                                    {stats.top_produits?.[0]?.designation || '-'}
                                                </h6>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col lg={activeTab === 'historique' ? 12 : 7}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white border-0 pt-4">
                                    <Tabs
                                        activeKey={activeTab}
                                        onSelect={(k) => setActiveTab(k)}
                                        className="border-0"
                                    >
                                        <Tab 
                                            eventKey="camera" 
                                            title={
                                                <span>
                                                    <i className="ri-camera-line me-2"></i>
                                                    Caméra
                                                </span>
                                            }
                                        >
                                            <ScannerCamera 
                                                onScan={handleScan}
                                                loading={loading}
                                                mode={mode}
                                            />
                                        </Tab>
                                        <Tab 
                                            eventKey="manuel" 
                                            title={
                                                <span>
                                                    <i className="ri-keyboard-line me-2"></i>
                                                    Saisie manuelle
                                                </span>
                                            }
                                        >
                                            <ScannerManuel 
                                                onScan={handleScan}
                                                loading={loading}
                                                mode={mode}
                                            />
                                        </Tab>
                                        <Tab 
                                            eventKey="historique" 
                                            title={
                                                <span>
                                                    <i className="ri-history-line me-2"></i>
                                                    Historique
                                                </span>
                                            }
                                        >
                                            {/* ✅ Passer la clé et la ref pour forcer le rafraîchissement */}
                                            <HistoriqueScans 
                                                key={historiqueKey}
                                                ref={historiqueRef}
                                            />
                                        </Tab>
                                    </Tabs>
                                </Card.Header>
                            </Card>
                        </Col>

                        {activeTab !== 'historique' && (
                            <Col lg={5}>
                                <ResultatScan
                                    scanResult={scanResult}
                                    loading={loading}
                                    error={error}
                                    alertes={alertes}
                                    mode={mode}
                                    quantite={quantite}
                                    setQuantite={setQuantite}
                                    commentaire={commentaire}
                                    setCommentaire={setCommentaire}
                                    onEntree={handleEntreeStock}
                                    onSortie={handleSortieStock}
                                    onVoirProduit={handleVoirProduit}
                                    getStockStatusColor={getStockStatusColor}
                                    getStockProgress={getStockProgress}
                                    formatCurrency={formatCurrency}
                                />
                            </Col>
                        )}
                    </Row>
                </div>
                <Footer />
            </div>
        </>
    );
}